import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import FadeIn from "@/components/FadeIn";
import { registerBrand } from "@/services/photoStorage";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Check, Clipboard, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Brand } from "@/types/photos";
import { generateUploadUrlLink } from "@/utils/routes";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [registeredBrand, setRegisteredBrand] = useState<{ name: string; id: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Create a new brand object
      const newBrand: Brand = {
        id: '',
        name,
        email,
        password,
        storeCredit: 0,
        createdAt: new Date().toISOString()
      };

      // Register the brand and get the response
      const registeredBrandData = await registerBrand(newBrand);

      // Ensure setup_completed is set to false in the database
      await supabase
        .from('brands')
        .update({ setup_completed: false })
        .eq('id', registeredBrandData.id);

      // Auto sign in the user
      await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      toast({
        title: "Account created!",
        description: "Your brand account has been created. Redirecting to setup wizard...",
      });

      // Reset form
      setName("");
      setEmail("");
      setPassword("");
      
      // Go directly to dashboard where setup wizard will show
      navigate("/dashboard");

    } catch (error) {
      console.error("Registration error:", error);
      toast({
        title: "Registration failed",
        description: error instanceof Error ? error.message : "There was a problem creating your account.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyUploadLink = () => {
    if (!registeredBrand) return;
    const uploadLink = generateUploadUrlLink(registeredBrand);
    navigator.clipboard.writeText(uploadLink);

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    toast({
      title: "Copied!",
      description: "Unique upload link copied to clipboard",
    });
  };

  const goToDashboard = async () => {
    // Navigate to dashboard - user is already signed in
    navigate("/dashboard");
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <FadeIn>
          {!registeredBrand ? (
            <Card className="w-full max-w-md">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold text-center">Create a brand account</CardTitle>
                <CardDescription className="text-center">
                  Enter your information to create your brand account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Brand Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your Brand Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Creating account..." : "Sign Up"}
                  </Button>
                </form>
              </CardContent>
              <CardFooter className="flex justify-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link to="/login" className="text-primary hover:underline">
                    Log in
                  </Link>
                </p>
              </CardFooter>
            </Card>
          ) : (
            <Card className="w-full max-w-md">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold text-center">Account Created!</CardTitle>
                <CardDescription className="text-center">
                  Your upload link is ready, and we'll guide you through the setup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-primary/10 border-primary/20">
                  <AlertDescription className="text-center text-primary">
                    Brand account for <span className="font-bold">{registeredBrand.name}</span> was successfully created
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label>Your Unique Upload Link</Label>
                  <div className="relative">
                    <Input
                      readOnly
                      value={generateUploadUrlLink(registeredBrand)}
                      className="pr-12 font-mono text-sm"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute right-1 top-1 h-7 w-7"
                      onClick={copyUploadLink}
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground pt-1">
                    Share this link with your customers to collect their photos
                  </p>
                </div>

                <div className="pt-2 space-y-2">
                  <Button onClick={goToDashboard} className="w-full">
                    Continue to Setup Wizard
                  </Button>
                  <div className="flex justify-center">
                    <Button variant="outline" size="sm" className="mt-2" asChild>
                      <Link to={generateUploadUrlLink(registeredBrand).replace(window.location.origin, '')} className="flex items-center gap-1">
                        <span>Preview Upload Page</span>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </FadeIn>
      </div>
      <Footer />
    </>
  );
};

export default Register;
